import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Formik, Form, yupToFormErrors } from 'formik';
import { v4 as uuid4 } from 'uuid';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { useOfferTasks } from 'offer/components/offer-onboarding-tasks/components/hooks/use-offer-tasks';
import { useBasisTheoryToken } from 'common/hooks/use-basis-theory-token';
import { ABODE_PRO_PLAN_AFFINITY, ABODE_PRO_PLAN_PROVIDER_NAME } from 'core/helpers/constants';
import { resolveMicroBiltBypass } from 'core/helpers/microbilt-bypass';
import { resolveCollectPaymentLater } from 'core/helpers/collect-payment-later';
import useSession from 'core/hooks/use-session';
import { Button } from 'core/components/button';
import { useToast } from 'core/components/toast';
import { Label } from 'core/components/label';
import { ValidationErrorsModal, useValidationErrorsModal } from 'core/components/validation-errors-modal';
import { useStore } from 'core/store';
import { OfferStatus } from 'core/store/offer-store';
import { awsDateTimeFormatter, phoneNumberFormatter } from 'core/helpers/formatters';
import { track } from 'core/helpers/analytics';
import { buildActorProperties, trackProspectFunnelEvent } from 'core/helpers/prospect-funnel';
import { OfferDialog } from 'offer/components/dialog';
import getQuoteFriendlySelectedOption from 'offer/helpers/quote-friendly-selected-option';
import useCheckoutContext from '../../hooks/use-checkout-context';
import { getSchema } from '../../checkout.validation-schema';
import { getPaymentMethods, getStressFreeSwitchBuyBindDetails, shouldShowStressFreeSwitch } from '../../helper';
import { BillingInformation } from '../billing-information';
import { SweepBindOption } from '../sweep-bind-option';
import { CancelCurrentPolicy } from '../cancel-current-policy';
import { PaymentsDisclosures } from '../payments-disclosures';
import { PreSaleChecklist } from '../pre-sale-checklist';
import { ExtraQuestions } from '../extra-questions';
import useFormInitialValues from './use-initial-form-values';
import { PurchaseLinkSection } from '../purchase-link-section';
import OfferOnboardingTasks from 'offer/components/offer-onboarding-tasks';
import useStyles from './checkout-form.styles';

const CheckoutForm = observer(({ disabled }) => {
  const classes = useStyles();
  const toast = useToast();
  const [checkingOut, setCheckingOut] = useState(false);
  const history = useHistory();
  const session = useSession();
  const {
    offer: {
      saveCheckoutFormData,
      offer,
      bindRequest,
      status,
      openDialog,
      triggerOfferDialog,
      selectedOption: selectedOptionFromStore
    },
    account
  } = useStore();
  const { canBindDirectly, isLicensedToPurchase } = useCheckoutContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();

  const { getBasisTheoryCCToken, getBasisTheoryACHToken } = useBasisTheoryToken();

  const selectedOption = getQuoteFriendlySelectedOption(selectedOptionFromStore);

  const showStressFreeSwitch = shouldShowStressFreeSwitch({
    selectedOption,
    noBindHome: offer?.quote?.noBindHome,
    noBindAuto: offer?.quote?.noBindAuto
  });

  const initialValues = useFormInitialValues(offer, selectedOption);
  const {
    tasks,
    incompleteTasks,
    completedTasks,
    loading: tasksLoading,
    error: tasksError,
    uploadDocument,
    deleteDocument
  } = useOfferTasks(offer.id, offer.webUserId, session?.user?.username);

  const { hasACH, hasCreditCard } = useMemo(() => {
    const { hasACH, hasCreditCard } = getPaymentMethods({
      paymentMethods: offer.quote.global,
      policyType: selectedOption,
      noBindHome: offer?.quote?.noBindHome,
      noBindAuto: offer?.quote?.noBindAuto,
      hasAbodeProPlan:
        offer?.quote?.global?.affinity === ABODE_PRO_PLAN_AFFINITY &&
        offer?.quote?.connectedHome?.providerName === ABODE_PRO_PLAN_PROVIDER_NAME
    });
    return { hasACH, hasCreditCard };
  }, [offer, selectedOption]);

  const validate = async (values) => {
    try {
      await getSchema(selectedOption, offer.quote, values, tasks, {
        isAgency: session.isAgency,
        canBind: canBindDirectly,
        canClearUWFormRejections: session.canClearUWFormRejections,
        offer
      }).validate(values, {
        abortEarly: false,
        context: values
      });
    } catch (error) {
      console.error(error);
      if (error?.name?.includes('TypeError')) {
        Sentry.captureException(error, {
          extra: {
            formValues: values,
            validationFailedToRun: true,
            inOfferCheckout: true
          }
        });
      } else {
        const formErrors = yupToFormErrors(error);
        setShowValidationErrorsModal(true);
        toast.notify({
          type: 'error',
          message: 'Please fix the form errors before continuing.',
          durationInSeconds: 3
        });
        console.log({ formErrors });
        return formErrors;
      }
    }
  };

  const onSubmit = useCallback(
    async (values) => {
      const option = offer.options.find((o) => o.type === selectedOption);
      const phoneNumber = phoneNumberFormatter({ phoneNumber: values.phone.toString() });
      const formattedAdditionalPhoneNumbers = values.additionalPhoneNumbers
        ?.filter((phoneDetail) => phoneDetail.phoneNumber)
        .map((phoneDetail) => ({
          phoneNumber: phoneNumberFormatter({ phoneNumber: phoneDetail.phoneNumber }),
          note: phoneDetail.note,
          canText: phoneDetail.canText
        }));

      // "Collect payment information later" (#33891): an underwriter chose to issue without
      // collecting payment now. Skip tokenization entirely (no card/bank details were entered) and
      // send the gated flag so the backend defers Stripe customer creation. Re-gated on the
      // underwriter permission here so a non-UW value can never be sent.
      const collectPaymentLater = resolveCollectPaymentLater(session.canApproveUWReview, values.collectPaymentLater);

      let creditCardInfo = null;
      if (hasCreditCard && !collectPaymentLater) {
        if (values.basisTheoryCardToken) {
          creditCardInfo = { token: values.basisTheoryCardToken };
        } else if (values.completeCardData) {
          try {
            creditCardInfo = await getBasisTheoryCCToken();
          } catch (error) {
            toast.notify({ type: 'error', message: error?.message ?? 'Card tokenization failed. Please try again.' });
            return;
          }
          saveCheckoutFormData({
            id: values.address,
            creditCardInfo: creditCardInfo
              ? { ...creditCardInfo, card: { ...creditCardInfo.card, last4: values.cardLast4 } }
              : null,
            basisTheoryError: null
          });
        }
      }

      let basisTheoryBankAccountTokenInfo = null;
      if (hasACH && !collectPaymentLater) {
        try {
          basisTheoryBankAccountTokenInfo = await getBasisTheoryACHToken();
        } catch (error) {
          toast.notify({ type: 'error', message: error.message });
          return;
        }
      }

      const email = values.email.trim();
      const stressFreeSwitchBuyBindDetails = getStressFreeSwitchBuyBindDetails({
        values,
        showStressFreeSwitch,
        includeUmbrella: offer.quote.includeUmbrella
      });
      const bindRequestInput = {
        id: uuid4(),
        offerId: offer.id,
        optionId: option.id,
        bindDetails: {
          emails: [{ email }],
          phoneNumbers: [{ number: phoneNumber }],
          additionalPhoneNumbers: formattedAdditionalPhoneNumbers,
          mailingAddress: { ...values.address, address2: values.address.address2 || null },
          drivers: values.drivers?.length
            ? values.drivers.map((driver) => ({
                id: driver.id,
                driversLicenseState: driver.driversLicenseState,
                driversLicenseNumber: driver.driversLicenseNumber
              }))
            : undefined,
          billingDayOfMonth: values.billingDayOfMonth,
          homeBillingDayOfMonth: values.homeBillingDayOfMonth,
          autoBillingDayOfMonth: values.autoBillingDayOfMonth,
          rentersBillingDayOfMonth: values.rentersBillingDayOfMonth,
          condoBillingDayOfMonth: values.condoBillingDayOfMonth,
          farmBillingDayOfMonth: values.farmBillingDayOfMonth,
          homeDownPayment: values.homeDownPayment,
          autoDownPayment: values.autoDownPayment,
          rentersDownPayment: values.rentersDownPayment,
          condoDownPayment: values.condoDownPayment,
          farmDownPayment: values.farmDownPayment,
          basisTheoryCardToken: creditCardInfo?.token,
          basisTheoryBankAccountToken: basisTheoryBankAccountTokenInfo?.id,
          // Agency sweep bind (#33155): pay the down payment from the agency's sweep account.
          // The card/bank tokens above still carry (and save) the customer's own method for
          // installments; the sweep PM is charged for the initial payment only.
          useSweepAccount: values.useSweepAccount || false,
          sweepPaymentMethodId: values.useSweepAccount ? values.sweepPaymentMethodId : null,
          salesRep: offer.quote.rep,
          bindRep: session.user.username,
          attestationsHomeAccepted: values.attestationsHomeAccepted ? awsDateTimeFormatter(new Date()) : null,
          attestationsAutoAccepted: values.attestationsAutoAccepted ? awsDateTimeFormatter(new Date()) : null,
          ...stressFreeSwitchBuyBindDetails,
          purchasedFromStaff: true,
          opposingPolicySoldByAgency: values.opposingPolicySoldByAgency,
          loanOfficerEmail: values.loanOfficerEmail,
          attestationsRentersAccepted: values.attestationsRentersAccepted ? awsDateTimeFormatter(new Date()) : null,
          attestationsCondoAccepted: values.attestationsCondoAccepted ? awsDateTimeFormatter(new Date()) : null,
          autoMegaDownPay: values.autoMegaDownPay,
          homeMegaDownPay: values.homeMegaDownPay,
          rentersMegaDownPay: values.rentersMegaDownPay,
          condoMegaDownPay: values.condoMegaDownPay,
          optIntoMarketingCampaigns: values.optIntoMarketingCampaigns,
          // Underwriter opt-in to hold the Rate Control Date (issue #32130). Set on the
          // Billing tab and carried on the offer store; lets the bind gate accept an RCD up
          // to the brand's underwriterMaxHoldDays (Y) instead of the default maxStaleDaysToBind.
          holdRateControlDate: offer.holdRateControlDate,
          // Underwriter opt-in (issue #33938): skip MicroBilt verification for a known-good ACH
          // account. The checkbox only renders for underwriters and the backend re-checks the
          // permission, but gate here too so a non-UW value can never be sent.
          bypassMicroBiltVerification: resolveMicroBiltBypass(
            session.canApproveUWReview,
            values.bypassMicroBiltVerification
          ),
          // Underwriter opt-in (issue #33891): issue without collecting payment now; the backend
          // (charge-policy-step) re-checks the permission + brand and defers Stripe customer creation.
          collectPaymentLater
        }
      };
      // Scalars only: track() drops object-valued properties (toFlatEventProperties).
      track('Staff Purchase Offer', {
        offerId: bindRequestInput.offerId,
        optionId: bindRequestInput.optionId,
        webUserId: offer.webUserId,
        selectedOption,
        collectPaymentLater,
        holdRateControlDate: offer.holdRateControlDate,
        bypassMicroBiltVerification: bindRequestInput.bindDetails.bypassMicroBiltVerification,
        state: offer.quote?.correctedAddress?.state,
        affinity: offer.quote?.global?.affinity,
        affinityStem: offer.quote?.global?.affinityGroupStem,
        isAgentSold: offer.quote?.isAgentSold,
        originallyFrom: offer.quote?.originallyFrom,
        ...buildActorProperties(session, offer.quote?.correctedAddress?.state)
      });
      // Dual emit: staff-identity Staff Purchase Offer (above) carries the bind-time
      // underwriter flags; the prospect-keyed funnel copy stays
      // lean (no PII) but carries the SFS opt-in state and a snapshot of
      // which onboarding tasks had documents uploaded at purchase time —
      // these replace the dedicated SFS Opt-in and Task Center Document
      // Uploaded funnel events.
      const uploadedTasks = (tasks || [])
        .filter((task) => task.data?.hasDocument)
        .map((task) => ({
          taskName: task.taskName,
          policyType: task.data?.policyType,
          customer: task.data?.customer
        }));
      trackProspectFunnelEvent({
        sendEventAsMember: account.sendEventAsMember,
        eventName: 'Staff Funnel: Purchase Button Clicked',
        offer,
        session,
        extraProps: {
          selectedOption,
          paymentMethod: [hasCreditCard && 'CC', hasACH && 'ACH'].find(Boolean) ?? null,
          breakupWithAuto: values.breakupWithAuto,
          breakupWithHomeowners: values.breakupWithHomeowners,
          breakupWithRenters: values.breakupWithRenters,
          breakupESignature: values.breakupESignature,
          uploadedTasks,
          uploadedTaskCount: uploadedTasks.length
        }
      });
      if (!values.address.address2 && values.noAptNumber) {
        await account.sendEventAsMember({
          eventName: 'AddressLine2 Attestation Checked',
          userId: offer.webUserId,
          properties: JSON.stringify({
            fromCheckout: true
          })
        });
      }
      const { id, systemId } = await bindRequest(bindRequestInput);
      if (id && systemId) {
        history.push({ pathname: `/offer/${offer.id}/purchased`, state: { email } });
      }
    },
    [
      offer,
      getBasisTheoryACHToken,
      saveCheckoutFormData,
      toast,
      getBasisTheoryCCToken,
      hasCreditCard,
      hasACH,
      session,
      selectedOption,
      showStressFreeSwitch,
      bindRequest,
      history
    ]
  );

  return (
    <Formik
      validateOnChange={false}
      validateOnBlur={false}
      initialValues={initialValues}
      validate={validate}
      onSubmit={async (values) => {
        setCheckingOut(true);
        try {
          if (canBindDirectly) {
            await onSubmit(values);
          }
        } catch (error) {
          // Safety net: any throw here would otherwise leave the Purchase button
          // permanently disabled (see #30829). Surface to the user + Sentry so
          // failures are never silent.
          Sentry.captureException(error);
          toast.notify({
            type: 'error',
            message: 'Something went wrong while purchasing. Please try again or refresh the page.'
          });
        } finally {
          setCheckingOut(false);
        }
      }}
    >
      <Form>
        <BillingInformation />
        {canBindDirectly && <SweepBindOption offer={offer} />}
        <PaymentsDisclosures policyType={selectedOption} offer={offer} />
        {showStressFreeSwitch && <CancelCurrentPolicy />}
        <PreSaleChecklist policyType={selectedOption} offer={offer} tasks={tasks} />
        <OfferOnboardingTasks
          offerId={offer.id}
          webUserId={offer.webUserId}
          tasks={tasks}
          incompleteTasks={incompleteTasks}
          completedTasks={completedTasks}
          tasksLoading={tasksLoading}
          tasksError={tasksError}
          uploadDocument={uploadDocument}
          deleteDocument={deleteDocument}
        />
        <ExtraQuestions offer={offer} isAgency={session.isAgency} />
        {!canBindDirectly && <PurchaseLinkSection toggleValidationErrorsModal={setShowValidationErrorsModal} />}
        {canBindDirectly && (
          <>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.purchaseButton}
              loading={status === OfferStatus.Purchasing || checkingOut}
              disabled={!isLicensedToPurchase || checkingOut || status === OfferStatus.Purchasing || disabled}
              fast={false}
            >
              Purchase
            </Button>
            {(!isLicensedToPurchase || disabled) && (
              <Label type="statusWarningMedium">
                Sorry, you are not licensed to purchase. If you believe this is an error, please follow your designated
                support flow to correct your user configuration and permissions.
              </Label>
            )}
          </>
        )}
        {openDialog && <OfferDialog onClose={() => triggerOfferDialog(false)} fromCheckout />}
        <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
      </Form>
    </Formik>
  );
});

CheckoutForm.propTypes = {
  disabled: PropTypes.bool.isRequired
};

export default CheckoutForm;
